<script lang="ts" setup>
import { defineEmits, defineProps, ref } from "vue";
import ArrowIcon from "@/components/icons/ArrowIcon.vue";

type Variant = "brown-outline" | "active" | "active-outline" | "white-outline";

const props = defineProps<{
  label: string;
  type: Variant;
  hoverType?: Variant;
  hoverContainer?: Variant;
}>();

const isHovered = ref(false);

const emit = defineEmits<{
  (e: "clickedBtn"): void;
}>();

function clicked() {
  emit("clickedBtn");
}

const btnClasses: Record<Variant, string> = {
  "brown-outline": "border border-brown text-brown",
  active: "bg-active text-white",
  "active-outline": "border border-active text-active",
  "white-outline": "border border-white text-white",
};

const btnHoverClasses: Record<Variant, string> = {
  "active-outline": "border hover:border-active hover:text-active",
  "brown-outline": "border hover:border-brown hover:text-brown",
  "white-outline": "border hover:border-white hover:text-white",
  active: "hover:bg-active hover:text-white hover:border-active",
};

const iconClasses: Record<Variant, string> = {
  "active-outline": "bg-active text-white",
  "brown-outline": "bg-brown text-white",
  "white-outline": "bg-white text-brown",
  active: "bg-white text-active",
};

const iconHoverClasses: Record<Variant, string> = {
  "active-outline": "group-hover:bg-active group-hover:text-white",
  "brown-outline": "group-hover:bg-brown group-hover:text-white",
  "white-outline": "group-hover:bg-white group-hover:text-brown",
  active: "group-hover:bg-white group-hover:text-active",
};

const btnHoverContainerClasses: Record<Variant, string> = {
  "active-outline":
    "group-hover/container:[&:not(:hover)]:border-active group-hover/container:[&:not(:hover)]:text-active",
  "brown-outline":
    "group-hover/container:[&:not(:hover)]:border-brown group-hover/container:[&:not(:hover)]:text-brown",
  "white-outline":
    "group-hover/container:[&:not(:hover)]:border-white group-hover/container:[&:not(:hover)]:text-white",
  active:
    "group-hover/container:[&:not(:hover)]:bg-active group-hover/container:[&:not(:hover)]:text-white group-hover/container:[&:not(:hover)]:border-active",
};

const iconHoverContainerClasses: Record<Variant, string> = {
  "active-outline":
    "group-hover/container:[&:not(:hover)]:bg-active group-hover/container:[&:not(:hover)]:text-white",
  "brown-outline":
    "group-hover/container:[&:not(:hover)]:bg-brown group-hover/container:[&:not(:hover)]:text-white",
  "white-outline":
    "group-hover/container:[&:not(:hover)]:bg-white group-hover/container:[&:not(:hover)]:text-brown",
  active:
    "group-hover/container:[&:not(:hover)]:bg-white group-hover/container:[&:not(:hover)]:text-active",
};
</script>

<template>
  <button
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
    @click="clicked"
    class="group btn"
    :class="[
      btnClasses[props.type],
      props.hoverContainer &&
        !isHovered &&
        btnHoverContainerClasses[props.hoverContainer],
      props.hoverType && btnHoverClasses[props.hoverType],
    ]"
  >
    <span class="flex-1 text-left transition-colors duration-300 ease-in-out">{{
      label
    }}</span>

    <span
      class="btn-text"
      :class="[
        iconClasses[props.type],
        props.hoverContainer &&
          !isHovered &&
          iconHoverContainerClasses[props.hoverContainer],
        props.hoverType && iconHoverClasses[props.hoverType],
      ]"
    >
      <ArrowIcon
        class="transition-transform duration-300 ease-in-out group-hover:rotate-45"
      />
    </span>
  </button>
</template>

<style scoped>
.btn {
  @apply relative flex items-center h-[46px] w-[173px] md:h-[56px] md:w-[225px] md:pl-6 pl-[15px] pr-1 rounded-full sf-pro font-semibold text-sm leading-[21px] md:font-medium md:text-base md:leading-100p transition-colors duration-300 ease-in-out;
}
.btn-text {
  @apply md:ml-4 ml-2 md:w-[46px] md:h-[46px] w-[38px] h-[38px] flex items-center justify-center rounded-full transition-colors duration-300 ease-in-out;
}
</style>
